import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare } from "lucide-react";
import { api, formatRelative } from "../lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "./ui/dropdown-menu";

const POLL_MS = 30_000;

/**
 * Bell icon with a count badge of unread @mention notifications.
 * Opens a dropdown listing each, clicking a row navigates to the bath record.
 */
export const MentionsBell = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const r = await api.get("/users/me/mentions/unread");
      setCount(r.data.count || 0);
      setItems(r.data.items || []);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, POLL_MS);
    const onFocus = () => fetchUnread();
    const onMentionsChanged = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    window.addEventListener("flowline:mentions-changed", onMentionsChanged);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("flowline:mentions-changed", onMentionsChanged);
    };
  }, [fetchUnread]);

  const openMention = async (m) => {
    setOpen(false);
    if (m?.record?.id) {
      navigate(`/bath/${m.record.id}`);
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.post("/users/me/mentions/read");
      await fetchUnread();
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={(v) => { setOpen(v); if (v) fetchUnread(); }}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors"
          aria-label={`${count} unread mention${count === 1 ? "" : "s"}`}
          data-testid="mentions-bell"
          title={count > 0 ? `You have ${count} unread mention${count === 1 ? "" : "s"}` : "No new mentions"}
        >
          <Bell className={`h-4 w-4 ${count > 0 ? "text-zinc-900" : "text-zinc-500"}`} />
          {count > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm ring-2 ring-white"
              data-testid="mentions-bell-count"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0" data-testid="mentions-dropdown">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between gap-2 bg-zinc-50">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-3.5 w-3.5 text-zinc-500" />
            <span className="font-heading font-bold text-sm tracking-tight text-zinc-950">
              You were tagged
            </span>
            {count > 0 && (
              <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold">
                {count}
              </span>
            )}
          </div>
          {count > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              disabled={loading}
              className="text-[11px] text-zinc-500 hover:text-zinc-900 disabled:opacity-50"
              data-testid="mentions-mark-all-read"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-96 overflow-auto">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center">
              <Bell className="h-8 w-8 text-zinc-200 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-xs text-zinc-500">No new mentions.</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                When a teammate tags you in a comment it&apos;ll show up here.
              </p>
            </div>
          )}
          {items.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => openMention(m)}
              className="w-full text-left px-4 py-3 border-b border-zinc-50 last:border-b-0 hover:bg-zinc-50 transition-colors block group"
              data-testid={`mention-item-${m.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-950 text-white text-[10px] font-bold flex items-center justify-center">
                  {initials(m.actor_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-semibold text-zinc-950 truncate">
                      {m.actor_name || "Someone"}
                    </span>
                    <span className="text-[10px] text-zinc-400 shrink-0">{formatRelative(m.timestamp)}</span>
                  </div>
                  {m.record && (
                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5 font-mono truncate">
                      {m.record.supplier_po || m.record.internal_po || "—"} · {m.record.description}
                    </div>
                  )}
                  <p className="text-xs text-zinc-700 mt-1 line-clamp-2 leading-snug">
                    {stripMentionTokens(m.snippet)}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function initials(name) {
  const parts = (name || "").trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function stripMentionTokens(text) {
  // @[Name](user:<id>) → @Name (for plain-text snippet display)
  return (text || "").replace(/@\[([^\]]+)\]\(user:[^\)]+\)/g, "@$1");
}
